"use client";

import { Fragment, useEffect, useState, type ReactNode } from "react";
import { setLocale, type Locale } from "@tami/core/intl";

import { detectClientLocale } from "@/lib/i18n/client-locale";

type LocaleProviderProps = {
  /** O idioma em que o servidor renderizou esta página. */
  readonly serverLocale: Locale;
  /**
   * Página pública traduzida (`/`, `/en`, `/es`): o idioma é o da URL e não
   * se troca no navegador. Quem escolhe outro idioma vai para outra URL.
   */
  readonly fixed?: boolean;
  readonly children: ReactNode;
};

/**
 * Deixa o `getLocale()` do navegador igual ao do servidor antes da hidratação
 * e corrige depois, se for o caso.
 *
 * Página dinâmica já chega no idioma certo (o servidor leu cookie e
 * `Accept-Language`). Página estática fora das públicas traduzidas chega no
 * idioma padrão: aqui o navegador descobre o idioma do visitante e remonta a
 * árvore nele. As públicas traduzidas (`fixed`) já vêm no idioma da URL.
 *
 * O `setLocale` roda no render, antes dos filhos, e só no navegador: no
 * servidor o idioma vem da requisição e um valor global misturaria visitantes.
 */
export function LocaleProvider({
  serverLocale,
  fixed = false,
  children,
}: LocaleProviderProps) {
  const [locale, setActiveLocale] = useState<Locale>(serverLocale);
  if (typeof window !== "undefined") setLocale(locale);

  useEffect(() => {
    if (fixed) return;
    const detected = detectClientLocale(
      document.cookie,
      navigator.languages ?? [navigator.language],
    );
    document.documentElement.lang = detected;
    if (detected !== locale) setActiveLocale(detected);
  }, [locale, fixed]);

  return <Fragment key={locale}>{children}</Fragment>;
}
